<template>
  <!-- 입고 핸드폰 촬영 페이지 — PC 입고처리 화면의 QR 로 연다. 로그인 없이 URL 의 토큰(2시간)으로만 동작한다 -->
  <div class="ipm">
    <div v-if="loading" class="ipm__state">加载中...<small>(불러오는 중)</small></div>
    <div v-else-if="fatal" class="ipm__state ipm__state--err">
      {{ fatal }}
    </div>
    <template v-else-if="item">
      <div class="ipm__head">
        <img v-if="item.이미지" :src="item.이미지" class="ipm__thumb" alt="">
        <div class="ipm__info">
          <div class="ipm__name">{{ item.중국어품명 || item.상품명 }}</div>
          <div v-if="item.옵션" class="ipm__opt">{{ item.옵션 }}</div>
          <div class="ipm__meta">{{ item.바코드 }} · {{ item.구매요청수량 }}</div>
        </div>
      </div>

      <div class="ipm__kinds">
        <button v-for="k in kinds" :key="k.value" type="button" class="ipm__kind"
                :class="{ 'ipm__kind--on': kind === k.value }" @click="kind = k.value">
          {{ k.cn }}<small>{{ k.ko }}</small>
        </button>
      </div>

      <!-- 연속 촬영: 페이지 안 카메라(getUserMedia)로 셔터만 누르면 바로 대기열에 들어간다(기본 카메라 앱의 「확인」 단계가 없다).
           업로드는 뒤에서 줄 서서 올라가고 버튼은 막지 않는다. 카메라를 못 쓰면 기존 파일 입력(기본 카메라 앱)으로 대체 -->
      <div class="ipm__actions">
        <button type="button" class="ipm__btn ipm__btn--main" :disabled="cameraStarting" @click="startCamera">
          📷 拍照<small>(촬영)</small>
        </button>
        <button type="button" class="ipm__btn" @click="$refs.album.click()">
          🖼 相册<small>(앨범)</small>
        </button>
        <input ref="cam" type="file" accept="image/*" capture="environment" style="display:none" @change="onFiles">
        <input ref="album" type="file" accept="image/*" multiple style="display:none" @change="onFiles">
      </div>

      <!-- 페이지 안 카메라 — 셔터마다 캡처해 바로 업로드 대기열로. 종류 선택은 여기서도 바꿀 수 있다 -->
      <div v-if="cameraOn" class="ipm-cam">
        <video ref="video" class="ipm-cam__video" autoplay playsinline muted></video>
        <div class="ipm-cam__top">
          <button v-for="k in kinds" :key="'c' + k.value" type="button" class="ipm-cam__kind"
                  :class="{ 'ipm-cam__kind--on': kind === k.value }" @click="kind = k.value">{{ k.cn }}</button>
        </div>
        <div class="ipm-cam__status">
          已拍<small>(촬영)</small> {{ shotCount }}张 ·
          <template v-if="pendingCount">上传中<small>(업로드 중)</small> {{ pendingCount }}</template>
          <template v-else>已全部上传<small>(업로드 완료)</small></template>
          <span v-if="failedCount" class="ipm-cam__fail"> · 失败<small>(실패)</small> {{ failedCount }}</span>
        </div>
        <div v-if="flash" class="ipm-cam__flash"></div>
        <div class="ipm-cam__bottom">
          <button type="button" class="ipm-cam__done" @click="stopCamera">完成<small>(완료)</small></button>
          <button type="button" class="ipm-cam__shutter" :disabled="!cameraReady" aria-label="shutter" @click="capture"></button>
          <span class="ipm-cam__spacer"></span>
        </div>
      </div>

      <div v-if="pendingCount" class="ipm__msg">上传中<small>(업로드 중)</small> {{ pendingCount }}张 · 可继续拍照<small>(계속 촬영 가능)</small></div>
      <div v-if="message" class="ipm__msg" :class="{ 'ipm__msg--err': messageErr }">{{ message }}</div>

      <div class="ipm__list-title">已上传<small>(업로드된 사진)</small> {{ photos.length }}</div>
      <div class="ipm__grid">
        <!-- 올라가는 중인 사진: 찍은 즉시 미리보기로 보인다 -->
        <div v-for="q in queueDesc" :key="q.key" class="ipm__cell ipm__cell--pending">
          <img :src="q.preview" alt="">
          <span class="ipm__tag" :class="'ipm__tag--' + kindCode(q.kind)">{{ kindCn(q.kind) }}</span>
          <span v-if="q.error" class="ipm__badge ipm__badge--err" @click="retry(q)">失败·重试<small>(재시도)</small></span>
          <span v-else class="ipm__badge">上传中…</span>
          <button v-if="q.error" type="button" class="ipm__del" @click="dropQueued(q)">✕</button>
        </div>
        <div v-for="(p, i) in photosDesc" :key="p.url + i" class="ipm__cell">
          <a :href="p.url" target="_blank" rel="noopener" class="ipm__link"><img :src="p.url" alt=""></a>
          <span class="ipm__tag" :class="'ipm__tag--' + kindCode(p.종류)">{{ kindCn(p.종류) }}</span>
          <span v-if="p.이전입고" class="ipm__prev">以前入库<small>(이전 입고)</small></span>
          <button type="button" class="ipm__del" :disabled="removing === p.url" @click="removePhoto(p)">✕</button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import { uploadImportPhoto } from '@/utils/importPhotoS3';

// 저장 키(value)는 그대로 두고 표기만 바꾼다 — 기존 사진 데이터가 이 키로 쌓여 있다
const KINDS = [
  { value: '입고사진', code: 'in', cn: '商品及包装照片', ko: '상품·포장 사진' },
  { value: '사이즈사진', code: 'size', cn: '尺寸照片', ko: '사이즈사진' },
  { value: '오류사진', code: 'err', cn: '错误照片', ko: '오류사진' },
  // 需跟客户确认(고객 확인 필요) 사진 — 2026-10-04 YZY
  { value: '확인사진', code: 'cf', cn: '需确认照片', ko: '확인필요' }
];
// 연속 촬영 캡처 — 긴 변 1920px JPEG(업로드 유틸의 축소 기준과 같다)
const CAPTURE_MAX_SIDE = 1920;
const CAPTURE_QUALITY = 0.85;

export default {
  name: 'ImportPhotoMobile',
  data() {
    const k = KINDS.find(x => x.code === this.$route.query.k);
    return {
      kinds: KINDS,
      kind: k ? k.value : '입고사진',
      loading: true,
      fatal: '',
      item: null,
      photos: [],
      // 업로드 대기열 { key, file, kind, preview, error } — 한 장씩 순서대로 올린다
      queue: [],
      working: false,
      removing: '',
      message: '',
      messageErr: false,
      // 페이지 안 카메라(연속 촬영)
      cameraOn: false,
      cameraReady: false,
      cameraStarting: false,
      cameraUnavailable: false, // 미지원·권한 거부 → 다음부터 파일 입력(기본 카메라 앱)
      stream: null,
      shotCount: 0,
      flash: false
    };
  },
  computed: {
    auth() {
      return { id: this.$route.params.id, e: this.$route.query.e, t: this.$route.query.t };
    },
    photosDesc() {
      return this.photos.slice().reverse();
    },
    queueDesc() {
      return this.queue.slice().reverse();
    },
    pendingCount() {
      return this.queue.filter(q => !q.error).length;
    },
    failedCount() {
      return this.queue.filter(q => q.error).length;
    }
  },
  watch: {
    // 링크 만료 등으로 화면이 닫히면 카메라도 끈다
    fatal(v) {
      if (v) this.stopCamera();
    }
  },
  mounted() {
    this.load();
  },
  beforeDestroy() {
    this.releaseStream();
    this.queue.forEach(q => this.revoke(q));
  },
  methods: {
    kindCode(v) { const k = KINDS.find(x => x.value === v); return k ? k.code : 'in'; },
    kindCn(v) { const k = KINDS.find(x => x.value === v); return k ? k.cn : v; },
    expiredText() {
      return '链接已失效，请在电脑上重新生成二维码 (링크가 만료됐습니다. PC 에서 QR 을 다시 띄워 주세요)';
    },
    async load() {
      this.loading = true;
      try {
        const { data } = await axios.post('/api/importPhoto/info', this.auth);
        if (data.요청결과 === '성공') {
          this.item = data.item;
          this.photos = data.item.입고사진 || [];
        } else {
          this.fatal = data.error && data.error.errorCode === '401' ? this.expiredText() : '加载失败(불러오기 실패): ' + ((data.error && data.error.errorMessage) || '');
        }
      } catch (e) {
        console.error('[importPhoto] info 실패', e);
        this.fatal = '网络错误(네트워크 오류): ' + e.message;
      } finally {
        this.loading = false;
      }
    },
    revoke(q) {
      try {
        URL.revokeObjectURL(q.preview);
      } catch (e) {
        console.error('[importPhoto] 미리보기 해제 실패', e);
      }
    },
    // 고르자마자 대기열에 넣고 바로 돌아온다 — 촬영 버튼은 막지 않는다
    onFiles(ev) {
      const files = Array.from(ev.target.files || []);
      ev.target.value = '';
      if (!files.length) return;
      this.message = '';
      this.messageErr = false;
      files.forEach(file => this.enqueue(file, this.kind));
    },
    enqueue(file, kind) {
      this.queue.push({
        key: Date.now() + '_' + Math.random().toString(36).slice(2, 8),
        file, kind, preview: URL.createObjectURL(file), error: ''
      });
      this.pump();
    },
    // 촬영 버튼 — 페이지 안 카메라를 켠다. 미지원이거나 한 번 실패했으면 기존 파일 입력(기본 카메라 앱)
    async startCamera() {
      const md = navigator.mediaDevices;
      if (this.cameraUnavailable || !md || typeof md.getUserMedia !== 'function') {
        this.$refs.cam.click();
        return;
      }
      this.cameraStarting = true;
      try {
        const stream = await md.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false
        });
        this.stream = stream;
        this.shotCount = 0;
        this.cameraOn = true;
        this.cameraReady = false;
        await this.$nextTick();
        const v = this.$refs.video;
        v.srcObject = stream;
        v.onloadedmetadata = () => { this.cameraReady = true; };
        await v.play();
      } catch (e) {
        // 권한 거부·카메라 없음 → 이후 촬영은 기본 카메라 앱으로. 비동기 뒤라 자동 클릭은 막히므로 한 번 더 누르게 안내한다
        console.error('[importPhoto] 카메라 시작 실패 — 파일 입력으로 대체', e);
        this.releaseStream();
        this.cameraOn = false;
        this.cameraUnavailable = true;
        this.messageErr = true;
        this.message = '无法使用相机，请再点一次「拍照」用系统相机 (카메라를 쓸 수 없습니다. 「촬영」을 한 번 더 누르면 기본 카메라로 찍습니다)';
      } finally {
        this.cameraStarting = false;
      }
    },
    // 셔터 — 지금 프레임을 JPEG 로 떠서 바로 업로드 대기열에 넣는다(확인 단계 없음)
    capture() {
      const v = this.$refs.video;
      if (!v || !v.videoWidth || !v.videoHeight) return;
      const scale = Math.min(1, CAPTURE_MAX_SIDE / Math.max(v.videoWidth, v.videoHeight));
      const w = Math.round(v.videoWidth * scale);
      const h = Math.round(v.videoHeight * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      canvas.getContext('2d').drawImage(v, 0, 0, w, h);
      const kind = this.kind;
      this.flash = true;
      setTimeout(() => { this.flash = false; }, 120);
      canvas.toBlob((blob) => {
        if (!blob) {
          console.error('[importPhoto] 캡처 실패 — toBlob null');
          this.messageErr = true;
          this.message = '拍照失败(촬영 실패)';
          return;
        }
        this.shotCount += 1;
        const file = new File([blob], 'cam_' + Date.now() + '.jpg', { type: 'image/jpeg' });
        this.enqueue(file, kind);
      }, 'image/jpeg', CAPTURE_QUALITY);
    },
    stopCamera() {
      this.releaseStream();
      this.cameraOn = false;
      this.cameraReady = false;
    },
    releaseStream() {
      if (this.stream) {
        this.stream.getTracks().forEach(t => {
          try { t.stop(); } catch (e) { console.error('[importPhoto] 카메라 정지 실패', e); }
        });
        this.stream = null;
      }
    },
    retry(q) {
      q.error = '';
      this.pump();
    },
    dropQueued(q) {
      this.revoke(q);
      this.queue = this.queue.filter(x => x !== q);
    },
    // 대기열을 한 장씩 비운다(S3 업로드 → 그 건에 붙이기). 실패한 장은 남겨 두고 다음 장으로 간다
    async pump() {
      if (this.working || this.fatal) return;
      this.working = true;
      try {
        let q;
        while (!this.fatal && (q = this.queue.find(x => !x.error))) {
          try {
            const url = await uploadImportPhoto(q.file, this.auth.id);
            const { data } = await axios.post('/api/importPhoto/add', { ...this.auth, 종류: q.kind, urls: [url] });
            if (data.요청결과 === '성공') {
              this.photos = data.입고사진 || this.photos;
              this.revoke(q);
              this.queue = this.queue.filter(x => x !== q);
              this.messageErr = false;
              this.message = `✅ ${this.kindCn(q.kind)} 上传完成 (업로드 완료)`;
            } else if (data.error && data.error.errorCode === '401') {
              this.fatal = this.expiredText();
            } else {
              q.error = (data.error && data.error.errorMessage) || 'fail';
              this.messageErr = true;
              this.message = '保存失败(저장 실패): ' + q.error;
            }
          } catch (e) {
            console.error('[importPhoto] 업로드 실패', e);
            q.error = e.message || 'fail';
            this.messageErr = true;
            this.message = '上传失败(업로드 실패): ' + q.error;
          }
        }
      } finally {
        this.working = false;
      }
    },
    async removePhoto(p) {
      if (!confirm('删除这张照片?(이 사진을 삭제할까요?)')) return;
      this.removing = p.url;
      try {
        const { data } = await axios.post('/api/importPhoto/remove', { ...this.auth, url: p.url });
        if (data.요청결과 === '성공') {
          this.photos = data.입고사진 || this.photos.filter(x => x.url !== p.url);
          this.messageErr = false;
          this.message = '🗑 已删除(삭제됨)';
        } else if (data.error && data.error.errorCode === '401') {
          this.fatal = this.expiredText();
        } else {
          this.messageErr = true;
          this.message = '删除失败(삭제 실패): ' + ((data.error && data.error.errorMessage) || '');
        }
      } catch (e) {
        console.error('[importPhoto] 삭제 실패', e);
        this.messageErr = true;
        this.message = '删除失败(삭제 실패): ' + e.message;
      } finally {
        this.removing = '';
      }
    }
  }
};
</script>

<style scoped>
.ipm { max-width: 560px; margin: 0 auto; padding: 12px; font-size: 16px; color: #212529; background: #fff; min-height: 100vh; }
.ipm__state { padding: 40px 12px; text-align: center; color: #6c757d; }
.ipm__state--err { color: #c0392b; }
.ipm__head { display: flex; gap: 10px; align-items: center; padding-bottom: 10px; border-bottom: 1px solid #e5e5e5; }
.ipm__thumb { width: 64px; height: 64px; object-fit: cover; border-radius: 6px; flex: none; }
.ipm__info { min-width: 0; }
.ipm__name { font-weight: 600; line-height: 1.3; word-break: break-all; }
.ipm__opt { color: #c0392b; font-weight: 600; font-size: 15px; }
.ipm__meta { color: #6c757d; font-size: 13px; }
.ipm__kinds { display: flex; gap: 6px; margin: 12px 0; }
.ipm__kind { flex: 1; padding: 10px 4px; border: 2px solid #ced4da; border-radius: 8px; background: #fff; font-size: 15px; font-weight: 600; }
.ipm__kind small { display: block; font-weight: 400; font-size: 11px; color: #6c757d; }
.ipm__kind--on { border-color: #007bff; background: #e8f1ff; color: #0056b3; }
.ipm__actions { display: flex; gap: 8px; }
.ipm__btn { flex: 1; padding: 16px 8px; border: 1px solid #ced4da; border-radius: 10px; background: #f8f9fa; font-size: 18px; font-weight: 600; }
.ipm__btn small { font-size: 12px; font-weight: 400; }
.ipm__btn--main { flex: 2; background: #007bff; border-color: #007bff; color: #fff; }
.ipm__btn:disabled { opacity: .5; }
.ipm__msg { margin-top: 10px; padding: 8px 10px; border-radius: 6px; background: #eafaf0; color: #1e7e34; font-size: 14px; }
.ipm__msg--err { background: #fdecea; color: #c0392b; }
.ipm__list-title { margin: 16px 0 6px; font-weight: 600; }
.ipm__grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; }
.ipm__cell { position: relative; display: block; padding-top: 100%; border-radius: 6px; overflow: hidden; background: #f1f1f1; }
.ipm__cell img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
.ipm__tag { position: absolute; left: 4px; bottom: 4px; padding: 1px 5px; border-radius: 4px; font-size: 11px; color: #fff; background: #007bff; }
.ipm__tag--err { background: #dc3545; }
.ipm__tag--size { background: #28a745; }
.ipm__tag--cf { background: #fd7e14; }
.ipm__link { position: absolute; inset: 0; }
.ipm__cell--pending img { opacity: .55; }
.ipm__del { position: absolute; right: 4px; top: 4px; width: 28px; height: 28px; padding: 0; border: none; border-radius: 50%; background: rgba(0, 0, 0, .6); color: #fff; font-size: 15px; line-height: 28px; }
.ipm__del:disabled { opacity: .4; }
.ipm__badge { position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%); padding: 2px 6px; border-radius: 4px; font-size: 12px; color: #fff; background: rgba(0, 0, 0, .55); white-space: nowrap; }
.ipm__badge small { font-size: 10px; }
.ipm__badge--err { background: #dc3545; }
.ipm__prev { position: absolute; right: 4px; bottom: 4px; padding: 1px 4px; border-radius: 4px; font-size: 10px; color: #fff; background: #6c757d; }
.ipm__prev small { font-size: 9px; }
.ipm-cam { position: fixed; inset: 0; z-index: 2000; background: #000; display: flex; flex-direction: column; }
.ipm-cam__video { flex: 1; width: 100%; min-height: 0; object-fit: contain; background: #000; }
.ipm-cam__top { position: absolute; top: 0; left: 0; right: 0; display: flex; gap: 6px; padding: 10px; background: linear-gradient(rgba(0, 0, 0, .6), rgba(0, 0, 0, 0)); }
.ipm-cam__kind { flex: 1; padding: 8px 2px; border: 2px solid rgba(255, 255, 255, .5); border-radius: 8px; background: rgba(0, 0, 0, .35); color: #fff; font-size: 13px; font-weight: 600; }
.ipm-cam__kind--on { border-color: #ffc107; color: #ffc107; }
.ipm-cam__status { position: absolute; left: 0; right: 0; bottom: 120px; text-align: center; color: #fff; font-size: 15px; text-shadow: 0 1px 2px #000; }
.ipm-cam__status small { font-size: 11px; }
.ipm-cam__fail { color: #ff6b6b; }
.ipm-cam__flash { position: absolute; inset: 0; background: rgba(255, 255, 255, .6); pointer-events: none; }
.ipm-cam__bottom { position: absolute; left: 0; right: 0; bottom: 0; height: 110px; display: flex; align-items: center; justify-content: space-between; padding: 0 24px; background: rgba(0, 0, 0, .5); }
.ipm-cam__done { width: 76px; padding: 10px 0; border: 1px solid #fff; border-radius: 8px; background: transparent; color: #fff; font-size: 16px; font-weight: 600; }
.ipm-cam__done small { display: block; font-size: 11px; font-weight: 400; }
.ipm-cam__shutter { width: 76px; height: 76px; border-radius: 50%; border: 6px solid rgba(255, 255, 255, .6); background: #fff; }
.ipm-cam__shutter:active { transform: scale(.92); }
.ipm-cam__shutter:disabled { opacity: .4; }
.ipm-cam__spacer { width: 76px; }
</style>
